import { api } from '@xtnd/web/shared/api';
import type {
  InvoiceApiResponse,
  InvoiceTransaction,
  TransactionDeliveryDetails,
  TransactionDeliverySummaryResponse,
  TransformedInvoice,
  InvoiceQueryParams,
  TransactionDocumentHistoryResponse,
} from '../types';

// Re-export types for backward compatibility
export type { InvoiceApiResponse, InvoiceTransaction, TransformedInvoice, InvoiceQueryParams };

class InvoiceApi {
  private readonly baseSource = '/juniper-query/transactions';

  // Optimized field selection for invoices list (excludes massive events array)
  private readonly invoiceFields = [
    // Core fields
    'juniperId',
    'status',
    'matterId',
    'matterDisplayNumber',
    'matterStatus',
    'matterType',
    'matterTypeStatus',
    'matterTypeMessage',
    'matterTypeMissingFields',
    'clientName',
    'trustRequestState',
    'trustRequestId',
    'trustRequestNumber',
    'createdAt',
    'updatedAt',
    'lastSentAt',
    'emailQueued',
    'emailProcessingStatus',
    'createdByUserId',
    'documents',
    // Operational Bill fields (only what we need)
    'operationalBill.id',
    'operationalBill.number',
    'operationalBill.total',
    'operationalBill.balance',
    'operationalBill.pending',
    'operationalBill.due',
    'operationalBill.state',
    'operationalBill.issued_at',
    'operationalBill.due_at',
    'operationalBill.created_at',
    'operationalBill.last_sent_at',
    'operationalBill.matters',
    'operationalBill.user.id',
    'operationalBill.paid',
    'operationalBill.paid_at',
    // Trust Request Bill fields (matching operational bill fields)
    'trustRequestBill.id',
    'trustRequestBill.number',
    'trustRequestBill.total',
    'trustRequestBill.balance',
    'trustRequestBill.due',
    'trustRequestBill.paid',
    'trustRequestBill.pending',
    'trustRequestBill.credits_issued',
    'trustRequestBill.state',
    'trustRequestBill.issued_at',
    'trustRequestBill.due_at',
    'trustRequestBill.created_at',
    'trustRequestBill.updated_at',
    'trustRequestBill.matters',
    'trustRequestBill.paid_at',
    // Bill recipient emails extracted from matter contacts
    'billRecipientEmails',
    // Trust request calculation data
    'calculation_data',
    // Payment link fields
    'clioPaymentLinkId',
    'clioPaymentLinkUrl',
    'clio_payment_link',
    // Last payment allocation fields
    'lastOperationalPaymentAt',
    'lastOperationalPaymentAmount',
    'lastTrustRequestPaymentAt',
    'lastTrustRequestPaymentAmount'
  ].join(',');

  /**
   * Get invoices with filtering and pagination
   */
  async getInvoices(params: InvoiceQueryParams = {}): Promise<InvoiceApiResponse> {
    const queryParams = new URLSearchParams();

    // Core parameters - no live data for invoices (slow-changing data)
    queryParams.set('live', 'false');
    // Use optimized field selection to exclude events and reduce response size
    queryParams.set('fields', this.invoiceFields);
    queryParams.set('limit', (params.limit || 100).toString());

    // Pagination
    if (params.cursor) {
      queryParams.set('cursor', params.cursor);
    }

    // Sorting - map frontend fields to valid backend enum values
    const backendSortField = this.mapSortField(params.sortBy || 'createdAt');
    queryParams.set('sortField', backendSortField);
    queryParams.set('sortDirection', params.sortDirection || 'desc');

    // Filtering
    if (params.statusFilter) {
      queryParams.set('status', params.statusFilter);
    }

    if (params.searchQuery) {
      queryParams.set('search', params.searchQuery);
    }

    if (params.dateFrom) {
      queryParams.set('dateFrom', params.dateFrom);
    }

    if (params.dateTo) {
      queryParams.set('dateTo', params.dateTo);
    }

    if (params.dateField) {
      queryParams.set('dateField', params.dateField);
    }

    if (params.uniqueByMatter !== undefined) {
      queryParams.set('uniqueByMatter', params.uniqueByMatter.toString());
    }

    if (params.createdByUserIds && params.createdByUserIds.length > 0) {
      queryParams.set('createdByUserIds', params.createdByUserIds.join(','));
    }

    if (params.matterId) {
      queryParams.set('matterId', params.matterId);
    }

    if (params.matterType && params.matterType !== 'all') {
      queryParams.set('matterType', params.matterType);
    }

    if (params.mainBuckets && params.mainBuckets.length > 0) {
      queryParams.set('mainBuckets', params.mainBuckets.join(','));
    }

    if (params.operationalStates && params.operationalStates.length > 0) {
      queryParams.set('operationalStates', params.operationalStates.join(','));
    }

    if (params.trustRequestStates && params.trustRequestStates.length > 0) {
      queryParams.set('trustRequestStates', params.trustRequestStates.join(','));
    }

    if (params.juniperStatuses && params.juniperStatuses.length > 0) {
      queryParams.set('juniperStatuses', params.juniperStatuses.join(','));
    }

    if (params.pastDue !== undefined) {
      queryParams.set('pastDue', params.pastDue ? 'true' : 'false');
    }

    if (params.noPaymentInDays) {
      queryParams.set('noPaymentInDays', params.noPaymentInDays.toString());
    }

    if (params.customFieldFilters && params.customFieldFilters.length > 0) {
      queryParams.set('customFieldFilters', JSON.stringify(params.customFieldFilters));
    }

    if (params.billNumber) {
      queryParams.set('billNumber', params.billNumber);
    }

    const url = `${this.baseSource}?${queryParams.toString()}`;
    const responseData = await api.get<InvoiceApiResponse>(url);

    return responseData;
  }

  /**
   * Get juniperIds for every transaction matching the supplied filters.
   * Powers the "select all matching" cross-pagination flow.
   */
  async getInvoiceIds(
    params: InvoiceQueryParams = {}
  ): Promise<{ ids: string[]; totalMatching: number; truncated: boolean }> {
    const queryParams = new URLSearchParams();
    queryParams.set('live', 'false');

    const backendSortField = this.mapSortField(params.sortBy || 'createdAt');
    queryParams.set('sortField', backendSortField);
    queryParams.set('sortDirection', params.sortDirection || 'desc');

    if (params.statusFilter) queryParams.set('status', params.statusFilter);
    if (params.searchQuery) queryParams.set('search', params.searchQuery);
    if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.set('dateTo', params.dateTo);
    if (params.dateField) queryParams.set('dateField', params.dateField);
    if (params.uniqueByMatter !== undefined) {
      queryParams.set('uniqueByMatter', params.uniqueByMatter.toString());
    }
    if (params.createdByUserIds && params.createdByUserIds.length > 0) {
      queryParams.set('createdByUserIds', params.createdByUserIds.join(','));
    }
    if (params.matterId) queryParams.set('matterId', params.matterId);
    if (params.matterType && params.matterType !== 'all') {
      queryParams.set('matterType', params.matterType);
    }
    if (params.mainBuckets && params.mainBuckets.length > 0) {
      queryParams.set('mainBuckets', params.mainBuckets.join(','));
    }
    if (params.operationalStates && params.operationalStates.length > 0) {
      queryParams.set('operationalStates', params.operationalStates.join(','));
    }
    if (params.trustRequestStates && params.trustRequestStates.length > 0) {
      queryParams.set('trustRequestStates', params.trustRequestStates.join(','));
    }
    if (params.juniperStatuses && params.juniperStatuses.length > 0) {
      queryParams.set('juniperStatuses', params.juniperStatuses.join(','));
    }
    if (params.pastDue !== undefined) {
      queryParams.set('pastDue', params.pastDue ? 'true' : 'false');
    }
    if (params.noPaymentInDays) {
      queryParams.set('noPaymentInDays', params.noPaymentInDays.toString());
    }
    if (params.customFieldFilters && params.customFieldFilters.length > 0) {
      queryParams.set('customFieldFilters', JSON.stringify(params.customFieldFilters));
    }
    if (params.billNumber) queryParams.set('billNumber', params.billNumber);

    const url = `${this.baseSource}/ids?${queryParams.toString()}`;
    return await api.get<{ ids: string[]; totalMatching: number; truncated: boolean }>(url);
  }

  /**
   * Get a single invoice by Juniper ID
   */
  async getInvoice(juniperId: string, options?: { live?: boolean; fields?: string }): Promise<any> {
    // Support live fetch and optional field selection
    const params: string[] = [];
    params.push(`live=${options?.live ? 'true' : 'false'}`);
    if (options?.fields) {
      params.push(`fields=${encodeURIComponent(options.fields)}`);
    }
    const url = `${this.baseSource}/${juniperId}?${params.join('&')}`;

    return api.get<any>(url);
  }

  async getDeliverySummary(juniperIds: string[]): Promise<TransactionDeliverySummaryResponse> {
    return api.post<TransactionDeliverySummaryResponse>('/juniper/transactions/delivery-summary', {
      juniperIds,
    });
  }

  async getDeliveryDetails(juniperId: string): Promise<TransactionDeliveryDetails> {
    return api.get<TransactionDeliveryDetails>(
      `/juniper/transactions/${juniperId}/delivery-details`
    );
  }

  async getTransactionDocumentHistory(juniperId: string): Promise<TransactionDocumentHistoryResponse> {
    return api.get<TransactionDocumentHistoryResponse>(
      `/juniper/transactions/${juniperId}/document-history`
    );
  }

  async getDocumentVersionDownload(
    juniperId: string,
    versionId: string
  ): Promise<{ signedUrl: string; filename: string; expiresIn: number; versionId: string }> {
    return api.get<{ signedUrl: string; filename: string; expiresIn: number; versionId: string }>(
      `/juniper/transactions/${juniperId}/document-history/${versionId}/download`
    );
  }

  /**
   * Transform API response to match UI structure
   */
  transformInvoice(transaction: InvoiceTransaction): TransformedInvoice {
    // Only destructure fields that actually exist in the API response
    const { juniperId, status, operationalBill, trustRequestState, updatedAt, trustRequestId, trustRequestNumber, clientName, createdAt, trustRequestBill } = transaction;

    // payment doesn't exist in current API response - handle gracefully
    const payment = (transaction as any).payment;

    return {
      id: juniperId,
      xtndTrustId: juniperId,
      shortJuniperId: juniperId.split('-')[1] || juniperId, // Extract segment after JUN-
      matterId:
        // 1) Prefer explicit matterId on root if present
        (transaction.matterId && String(transaction.matterId)) ||
        // 2) Fallback to operational bill's first matter id if available
        (operationalBill?.matters && operationalBill.matters.length > 0
          ? String(operationalBill.matters[0]?.id)
          : undefined) ||
        // 3) Fallback to any event-derived matter id
        this.extractMatterIdFromEvents(transaction) ||
        'Unknown',
      matterName: this.deriveMatterName(transaction),
      matterStatus: transaction.matterStatus,
      matterType: transaction.matterType,
      matterTypeStatus: transaction.matterTypeStatus,
      matterTypeMessage: transaction.matterTypeMessage,
      matterTypeMissingFields: transaction.matterTypeMissingFields,
      client: clientName || 'Unknown Client',
      recipients: this.deriveRecipients(transaction),
      billRecipientEmails: transaction.billRecipientEmails, // Pass through for email sending
      openingBalance: 0, // May not be available in current API
      operationalBill: {
        id: operationalBill.id, // Include ID for Clio links
        invoiceNumber: operationalBill.number,
        issuedDate: operationalBill.issued_at?.split('T')[0] || 'Unknown', // Convert to date only
        amount: operationalBill.total,
        balance: operationalBill.balance,
        pending: operationalBill.pending,
        due: operationalBill.due,
        status: this.mapBillStatus(operationalBill.state),
        state: operationalBill.state || 'draft', // Raw operational bill state
        approvalDate: operationalBill.created_at?.split('T')[0] || 'Unknown', // Use created_at as fallback
        dueDate: operationalBill.due_at?.split('T')[0] || undefined, // Due date for conditional formatting
        paid: operationalBill.paid,
        // Preserve user field for filtering (operationalBill.user.id)
        user: operationalBill.user ? { id: operationalBill.user.id } : undefined
      },
      allocations: [], // May need to be derived from line items or separate API
      trustRequest: {
        id: trustRequestId || (trustRequestBill?.id ? String(trustRequestBill.id) : '') || `TRR-${juniperId.split('-')[1] || 'Unknown'}`, // TRR ID from API or fallback
        number: trustRequestNumber || trustRequestBill?.number || '', // Do not derive; leave empty when not provided so UI can show N/A
        // Only populate trust request data when trustRequestState is NOT "not_created"
        date: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.issued_at?.split('T')[0] || '') : '',
        amount: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.total || 0) : 0,
        paid: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.paid || 0) : 0,
        balance: trustRequestBill?.balance,
        pending: trustRequestBill?.pending,
        due: trustRequestBill?.due as number | undefined,
        total: trustRequestBill?.total || 0,
        issued_at: trustRequestBill?.issued_at || '',
        due_at: trustRequestBill?.due_at || '',
        status: (trustRequestState && trustRequestState !== 'not_created') ? (trustRequestBill?.state || 'draft') : 'not_created',
        // Set trrStatus based on actual trust request state, not juniper transaction status
        trrStatus: (trustRequestState === 'not_created') ? 'Not Created' : (trustRequestState || trustRequestBill?.state || 'draft'),
        state: trustRequestState || trustRequestBill?.state || 'draft', // Raw trust request state from API
      },
      juniperStatus: status, // Main juniper transaction status
      payment: {
        destinationAccount: payment?.destinationAccount || 'Unknown',
        paymentLink: payment?.paymentLink || '',
        expiresOn: payment?.expiresOn ?? (operationalBill.due_at?.split('T')[0] ?? 'Unknown'),
      },
      status: this.mapTransactionStatus(status),
      sentOn: operationalBill.last_sent_at?.split('T')[0] ?? operationalBill.issued_at?.split('T')[0] ?? 'Unknown',
      createdAt: createdAt || operationalBill.created_at || new Date().toISOString(), // Transaction creation timestamp
      updatedAt: updatedAt || operationalBill.created_at || new Date().toISOString(), // Fallback to created_at or current time
      lastSentAt: (transaction as any).lastSentAt || undefined, // Add lastSentAt from transaction
      // Trust calculation data from calculation_data object
      calculationData: transaction.calculation_data ? {
        minimumThreshold: transaction.calculation_data.minimum_threshold,
        trustBalances: transaction.calculation_data.trust_balances,
        // Prefer `total_due` (includes interest) over `outstanding_balances` to ensure accurate totals
        outstandingBalances: transaction.calculation_data.total_due ?? transaction.calculation_data.outstanding_balances ?? transaction.calculation_data.outstandingBalance ?? undefined,
        // Include stored calculated trust amounts when present (support different naming variants)
        calculatedTrustAmountOpenMatter: transaction.calculation_data.calculatedTrustAmountOpenMatter ?? transaction.calculation_data.calculated_trust_amount_open_matter ?? undefined,
        calculatedTrustAmountClosedMatter: transaction.calculation_data.calculatedTrustAmountClosedMatter ?? transaction.calculation_data.calculated_trust_amount_closed_matter ?? undefined,
        formula: transaction.calculation_data.formula,
        calculatedAt: transaction.calculation_data.calculated_at,
      } : undefined,
      // Extract and sanitize pdfs from documents.storedAssets.pdfs for easier access
      pdfs: transaction.documents?.storedAssets?.pdfs ? {
        invoice: transaction.documents.storedAssets.pdfs.invoice ? {
          uploadedAt: transaction.documents.storedAssets.pdfs.invoice.uploadedAt,
          filename: transaction.documents.storedAssets.pdfs.invoice.filename,
          size: transaction.documents.storedAssets.pdfs.invoice.size,
          contentType: transaction.documents.storedAssets.pdfs.invoice.contentType,
        } : undefined,
      } : undefined,
      documents: transaction.documents,
      // Payment link properties from API response
      clioPaymentLinkId: transaction.clioPaymentLinkId,
      clioPaymentLinkUrl: transaction.clioPaymentLinkUrl,
      clio_payment_link: transaction.clio_payment_link,
      // Last payment date from bill paid_at
      lastOperationalPaymentAt: operationalBill?.paid_at || undefined,
      lastOperationalPaymentAmount: (transaction as any).lastOperationalPaymentAmount,
      lastTrustRequestPaymentAt: trustRequestBill?.paid_at || undefined,
      lastTrustRequestPaymentAmount: (transaction as any).lastTrustRequestPaymentAmount,
      // Unresolved master-data errors / other transaction-level issues that
      // require admin attention — surfaced in the warning column + Alert card.
      exceptions: (transaction as any).exceptions,
      // Pass through TRR replacement state + history so the History tab in
      // the transaction drawer can surface in-flight remediations and the
      // trail of prior TRRs replaced on this transaction.
      trustRequestHistory: (transaction as any).trustRequestHistory,
      trrReplacement: (transaction as any).trrReplacement,
      processingAction: (transaction as any).processingAction,
      processingState: (transaction as any).processingState,
      processingJobId: (transaction as any).processingJobId,
      processingQueuedAt: (transaction as any).processingQueuedAt,
      processingExpiresAt: (transaction as any).processingExpiresAt,
      processingError: (transaction as any).processingError,
    };
  }

  /**
   * Transform array of transactions for UI
   */
  transformInvoices(transactions: InvoiceTransaction[]): TransformedInvoice[] {
    return transactions.map(transaction => this.transformInvoice(transaction));
  }

  // Helper methods for data transformation
  private mapSortField(frontendField: string): string {
    // Map frontend sort fields to valid backend enum values
    const sortFieldMap: Record<string, string> = {
      'matterName': 'createdAt', // Sort by creation time since matter name isn't sortable on backend
      'client': 'createdAt', // Sort by creation time since client name isn't sortable on backend
      'trrStatus': 'status', // Map to juniper status field
      'juniperStatus': 'status', // Direct mapping
      'createdAt': 'createdAt', // Direct mapping
      'updatedAt': 'updatedAt', // Direct mapping
      'juniperId': 'juniperId', // Direct mapping
      'status': 'status', // Direct mapping
      'lastSentAt': 'lastSentAt',
      'operationalBillBalance': 'operationalBillBalance',
      'trustRequestBalance': 'trustRequestBalance',
      'lastOperationalPaymentAt': 'lastOperationalPaymentAt',
      'lastTrustRequestPaymentAt': 'lastTrustRequestPaymentAt'
    };

    return sortFieldMap[frontendField] || 'createdAt'; // Default to createdAt for unknown fields
  }

  private mapBillStatus(state: string): string {
    // Return the raw Clio bill state as-is
    // Valid states: draft, paid, awaiting_approval, awaiting_payment, voided, deleted
    return state || 'draft';
  }

  private mapTransactionStatus(status: string): string {
    return status.toLowerCase().includes('completed') ? 'completed' : 'active';
  }

  // Helper methods for status formatting removed - handled in UI component

  // Helper method removed - alert logic now handles time-based conditions

  private deriveMatterName(transaction: InvoiceTransaction): string {
    // Use matterDisplayNumber from root level - this is the correct field
    if (transaction.matterDisplayNumber && transaction.matterDisplayNumber.trim()) {
      return transaction.matterDisplayNumber;
    }

    return 'MISSING';
  }

  private deriveRecipients(transaction: InvoiceTransaction): string {
    // Use pre-extracted bill recipient emails from transaction
    if (transaction.billRecipientEmails && transaction.billRecipientEmails.length > 0) {
      // Handle both formats: array of strings or array of objects
      const emailStrings = transaction.billRecipientEmails
        .map((recipient: any) => {
          // If recipient is a string, use it directly
          if (typeof recipient === 'string') {
            return recipient;
          }
          // If recipient is an object, extract primary_email_address
          if (typeof recipient === 'object' && recipient !== null) {
            return recipient.primary_email_address;
          }
          return null;
        })
        .filter(Boolean) as string[];
      return emailStrings.join(', ');
    }

    // Fallback to client name if no bill recipients found
    return transaction.clientName || 'No recipients';
  }

  private extractMatterIdFromEvents(_transaction: InvoiceTransaction): string | null {
    // Events are excluded from API response for performance - this is now a no-op fallback
    // Matter ID should be available directly on the transaction or in operationalBill.matters
    return null;
  }
}

export const invoiceApi = new InvoiceApi();
